import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  BellAlertIcon,
  BoltIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  DocumentTextIcon,
  FolderPlusIcon,
  LightBulbIcon,
  PencilSquareIcon,
  PlayIcon,
  StopIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

import { formatRelativeTime } from './time';
import type { FeedItem } from './types';

type FeedIcon = ComponentType<SVGProps<SVGSVGElement>>;

export const describeFeedItem = (item: FeedItem): { icon: FeedIcon; text: string } => {
  const who = item.actorName ?? 'Niekto';
  const proj = item.projectName ? ` na ${item.projectName}` : '';
  switch (item.type) {
    case 'test.result': return {icon: ClipboardDocumentListIcon, text: `${who} otestoval postup${proj}: ${{pass:'funguje',fail:'nefunguje',blocked:'blokované',partial:'čiastočne',obsolete:'neaktuálne'}[String(item.payload.status)] ?? 'výsledok uložený'}`};
    case 'release.verified': return {icon: CheckCircleIcon, text: `${who} overil vydanie${proj}`};
    case 'knowledge.updated': return {icon: DocumentTextIcon, text: `${who} upravil dokumentáciu${proj}`};
    case 'session.started':
      return { icon: PlayIcon, text: `${who} začal pracovať${proj}` };
    case 'session.stopped':
      return { icon: StopIcon, text: `${who} ukončil prácu${proj}` };
    case 'session.git_push_eta':
      return { icon: ArrowPathIcon, text: `${who} pushol zmeny na git${proj}` };
    case 'git.push': {
      const actor = item.actorName || (item.payload.actor as string) || 'Niekto';
      const n = (item.payload.commitCount as number) ?? 0;
      const branch = (item.payload.branch as string) || '';
      return {
        icon: ArrowPathIcon,
        text: `${actor} pushol ${n} ${n === 1 ? 'commit' : 'commitov'}${branch ? ` → ${branch}` : ''}${proj}`,
      };
    }
    case 'git.pr_opened':
      return {
        icon: ArrowsRightLeftIcon,
        text: `${item.actorName || (item.payload.actor as string) || 'Niekto'} otvoril PR #${(item.payload.number as number) ?? ''}${proj}`,
      };
    case 'git.pr_merged':
      return {
        icon: CheckCircleIcon,
        text: `${item.actorName || (item.payload.actor as string) || 'Niekto'} zmergol PR #${(item.payload.number as number) ?? ''}${proj}`,
      };
    case 'git.pr_closed':
      return {
        icon: StopIcon,
        text: `${item.actorName || (item.payload.actor as string) || 'Niekto'} zavrel PR #${(item.payload.number as number) ?? ''}${proj}`,
      };
    case 'task.created':
      return { icon: ClipboardDocumentListIcon, text: `${who} vytvoril úlohu` };
    case 'task.updated':
      return item.payload?.completed === true
        ? { icon: CheckCircleIcon, text: `${who} dokončil úlohu` }
        : { icon: PencilSquareIcon, text: `${who} upravil úlohu` };
    case 'note.created':
      return { icon: DocumentTextIcon, text: `${who} pridal poznámku` };
    case 'expense.created':
      return { icon: CurrencyEuroIcon, text: `${who} pridal výdavok` };
    case 'handoff.requested':
      return { icon: ArrowsRightLeftIcon, text: `${who} požiadal o odovzdanie${proj}` };
    case 'handoff.accepted':
      return { icon: ArrowsRightLeftIcon, text: `${who} prijal odovzdanie${proj}` };
    case 'handoff.declined':
      return { icon: ArrowsRightLeftIcon, text: `${who} odmietol odovzdanie${proj}` };
    case 'handoff.cancelled':
      return { icon: ArrowsRightLeftIcon, text: `${who} zrušil odovzdanie${proj}` };
    case 'plan.created':
      return { icon: CalendarDaysIcon, text: `${who} si naplánoval prácu${proj}` };
    case 'plan.updated':
      return { icon: CalendarDaysIcon, text: `${who} upravil plán${proj}` };
    case 'plan.deleted':
      return { icon: CalendarDaysIcon, text: `${who} zrušil plán${proj}` };
    case 'project.created':
      return {
        icon: FolderPlusIcon,
        text: `${who} založil projekt ${item.projectName ?? ''}`.trim(),
      };
    case 'project.member_added':
      return { icon: UserPlusIcon, text: `${who} pridal člena${proj}` };
    case 'user.ping':
      return { icon: BellAlertIcon, text: `${who} poslal ping` };
    case 'idea.created':
      return {
        icon: LightBulbIcon,
        text: `${who} pridal nápad „${(item.payload.title as string) ?? ''}“`,
      };
    case 'idea.converted':
      return {
        icon: LightBulbIcon,
        text: `${who} spravil z nápadu ticket T-${(item.payload.ticketNumber as number) ?? ''}`,
      };
    case 'idea.commented':
      return { icon: LightBulbIcon, text: `${who} komentoval nápad` };
    default:
      return { icon: BoltIcon, text: `${who} · ${item.type}` };
  }
};

export const FeedRow = ({ item }: { item: FeedItem; avatarUrl?: string | null }) => {
  const { icon: Icon, text } = describeFeedItem(item);
  const iso = item.createdAt.includes('T')
    ? item.createdAt
    : item.createdAt.replace(' ', 'T') + 'Z';

  const content = (
    <>
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px] text-slate-800 dark:text-slate-100">
        {text}
      </span>
      <span className="flex-none text-[13px] text-slate-400 dark:text-slate-500">
        {formatRelativeTime(iso)}
      </span>
    </>
  );

  if (item.projectId) {
    return (
      <Link to={`/projects/${item.projectId}${item.type === "test.result" ? "?tab=testy" : item.type === "release.verified" ? "?tab=vydania" : item.type === "knowledge.updated" ? `?tab=dokumentacia&doc=${encodeURIComponent(item.entityId)}` : ""}`} className="list-row list-row-tappable">
        {content}
      </Link>
    );
  }

  return <div className="list-row">{content}</div>;
};
